import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, App, Button, Drawer, Form, Select, Skeleton, Space } from 'antd';
import type { DealItem, FieldDefinitionDto } from '@crm/shared';
import { RecordForm } from '../../components/RecordForm';
import { fromFormValues, toFormValues } from '../../components/DynamicField';
import { useFields } from '../../features/fields/api';
import { useCreateDeal, usePipelines, useUpdateDeal } from '../../features/deals/api';
import { errorMessage } from '../../api/client';

interface Props {
  open: boolean;
  onClose: () => void;
  deal?: DealItem | null;
  preset?: { pipelineId?: string; stageId?: string; contactId?: string; organizationId?: string };
  onSaved?: (d: DealItem) => void;
}

const HIDDEN = ['pipeline_id', 'stage_id', 'lost_reason', 'created_at', 'updated_at'];

export function DealDrawer({ open, onClose, deal, preset, onSaved }: Props) {
  const { message } = App.useApp();
  const defs = useFields('DEAL');
  const pipelines = usePipelines();
  const create = useCreateDeal();
  const update = useUpdateDeal();
  const [form] = Form.useForm();
  const [pipelineId, setPipelineId] = useState<string>();
  const [stageId, setStageId] = useState<string>();
  const latest = useRef({ defs: defs.data ?? [], preset });
  latest.current = { defs: defs.data ?? [], preset };
  // which record the form was last filled for; filling waits for field definitions to load
  const filledFor = useRef<string | null>(null);
  const defsReady = !!defs.data;

  useEffect(() => {
    if (!open) {
      filledFor.current = null;
      return;
    }
    const key = deal?.id ?? 'new';
    if (!defsReady || filledFor.current === key) return;
    filledFor.current = key;
    form.resetFields();
    const d = latest.current.defs as FieldDefinitionDto[];
    if (deal) {
      form.setFieldsValue(toFormValues(d, deal as unknown as Record<string, unknown>));
      setPipelineId(deal.pipelineId);
      setStageId(deal.stageId);
    } else {
      const p = latest.current.preset;
      form.setFieldsValue({ fields: {}, contactId: p?.contactId, organizationId: p?.organizationId });
      setPipelineId(p?.pipelineId);
      setStageId(p?.stageId);
    }
  }, [open, deal?.id, defsReady, form]); // eslint-disable-line react-hooks/exhaustive-deps

  // new deals default to the default pipeline once pipelines have loaded
  useEffect(() => {
    if (open && !deal && !pipelineId && pipelines.data?.length) setPipelineId(pipelines.data.find((x) => x.isDefault)?.id ?? pipelines.data[0].id);
  }, [open, deal, pipelineId, pipelines.data]);

  const pipeline = useMemo(() => pipelines.data?.find((p) => p.id === pipelineId), [pipelines.data, pipelineId]);
  const stages = pipeline?.stages ?? [];
  const effectiveStage = stages.some((s) => s.id === stageId) ? stageId : stages.find((s) => s.kind === 'OPEN')?.id;

  const submit = async (values: Record<string, unknown>) => {
    const payload = fromFormValues(defs.data ?? [], values) as Record<string, unknown>;
    delete payload.pipelineId;
    delete payload.stageId;
    try {
      let saved: DealItem;
      if (deal) {
        saved = await update.mutateAsync({ id: deal.id, ...payload, ...(effectiveStage && effectiveStage !== deal.stageId ? { stageId: effectiveStage } : {}) });
      } else {
        saved = await create.mutateAsync({ ...payload, pipelineId, stageId: effectiveStage } as Parameters<typeof create.mutateAsync>[0]);
      }
      message.success(deal ? 'Deal updated' : 'Deal created');
      onSaved?.(saved);
      onClose();
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <Drawer
      title={deal ? 'Edit deal' : 'New deal'}
      open={open}
      onClose={onClose}
      size={640}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={() => form.submit()} loading={create.isPending || update.isPending} disabled={!defsReady || !effectiveStage}>Save</Button>
        </Space>
      }
    >
      {pipeline && !effectiveStage && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title="This pipeline has no open stages. Add one under Admin → Pipelines." />}
      <Form form={form} layout="vertical" onFinish={submit} requiredMark={false}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 16 }}>
          <Form.Item label="Pipeline">
            <Select value={pipelineId} loading={pipelines.isLoading} disabled={!!deal} onChange={(v) => { setPipelineId(v); setStageId(undefined); }} options={(pipelines.data ?? []).map((p) => ({ value: p.id, label: p.name }))} />
          </Form.Item>
          <Form.Item label="Stage">
            <Select value={effectiveStage} onChange={setStageId} options={stages.map((s) => ({ value: s.id, label: s.name }))} />
          </Form.Item>
        </div>
        {defsReady ? <RecordForm defs={defs.data ?? []} form={form} hidden={HIDDEN} /> : <Skeleton active />}
      </Form>
    </Drawer>
  );
}
